const BANK_NAMES = {
	9181: 'Betri',
	9865: 'Norðoyar Sparikassi',
	6460: 'BankNordik',
	9870: 'Suðuroyar Sparikassi',
};

const NO_BENEFICIAL_OWNER_FIELD =
	'Sæt kryds her hvis der ikke findes nogen fysisk person der direkte eller indirekte ejer eller kontrollerer mere end 25  af virksomheden';

function fileHaystack(file) {
	return `${file.path || ''} ${file.name || ''} ${file.group || ''}`;
}

export function originalFileMatches(originalFiles, pattern) {
	if (!originalFiles || !originalFiles.length) {
		return false;
	}

	return originalFiles.some((file) => pattern.test(fileHaystack(file)));
}

export function hasEcomDocument(originalFiles) {
	return originalFileMatches(originalFiles, /e-?com|nethandil/i);
}

export function hasPosDocument(originalFiles) {
	return originalFileMatches(
		originalFiles,
		/swedbank_small|swedbank_large|butik-indl[oø]sning|\bpos\b|kortindl[oø]sning|\bcat\b|automat/i
	);
}

export function hasKycDocument(originalFiles) {
	return originalFileMatches(originalFiles, /kyc/i);
}

function emptyToNull(value) {
	if (value === undefined || value === null || value === '') {
		return '';
	}

	return value;
}

function phoneDigits(phoneNumber) {
	if (!phoneNumber) {
		return '';
	}

	return String(phoneNumber)
		.replace(/^\+298/, '')
		.replace(/\s+/g, '')
		.trim();
}

/**
 * Map customer, persons and bank account onto Swedbank/KYC AcroForm field names.
 * The backend fills original PDFs from agreement.data keys that match PDF field names.
 */
export function buildPdfFillData({ customer = {}, owners = [], bankAccount = null }) {
	const data = {};

	const name = emptyToNull(customer.name);
	const vtal = emptyToNull(customer.vtal);
	const address = emptyToNull(customer.address);
	const zip = emptyToNull(customer.zip);
	const city = emptyToNull(customer.city);
	const email = emptyToNull(customer.email);
	const phone = phoneDigits(customer.phoneNumber);
	const taxationCountry = emptyToNull(customer.taxationCountry);

	data['Virksomhedens juridiske navn'] = name;
	data['CVRnr'] = vtal;
	data['Postadresse'] = address;
	data['Postnummer og By'] = [zip, city].filter(Boolean).join(' ');
	data['Email'] = email;
	data['Area code'] = '298';
	data['Telefon area code'] = '298';
	data['TelefonMobil 45'] = phone;
	data['TelefonMobil'] = phone;

	data['FIRMANAVN/JURIDISK NAVN'] = name;
	data['CVR-NUMMER'] = vtal;
	data['ANGIV SKATTELAND'] = taxationCountry;
	data['TELEFON'] = phone;
	data['E-POST'] = email;
	data['BRANCHE/VIRKSOMHEDENS PRIMÆRE FORRETNINGSOMRÅDE'] = emptyToNull(customer.branch);
	data['HVAD ER DEN PRIMÆRE ÅRSAG TIL DIN FORRETNINGSRELATION MED SWEDBANK PAY'] = emptyToNull(
		customer.swedbankPayReason
	);

	data['Brugerens for og efternavn'] = emptyToNull(customer.tcs_username);
	data['Brugerens emailadresse'] = emptyToNull(customer.tcs_email);

	const tcsName = String(customer.tcs_username || '')
		.trim()
		.split(/\s+/);
	data['Brugerens fornavn'] = tcsName[0] || '';
	data['Brugerens efternavn'] = tcsName.slice(1).join(' ');

	const signatures = (owners || []).filter((owner) => owner.signature);
	const beneficialOwners = (owners || []).filter((owner) => {
		const share = Number(owner.ownership);
		return owner.isOwner && !Number.isNaN(share) && share >= 25;
	});
	const contactPerson = (owners || []).find((owner) => owner.isContactPerson);

	for (let i = 0; i < 3; i += 1) {
		data[`CPRnummer${i === 0 ? '' : `_${i + 1}`}`] = '';
		data[`Navn ${i + 1} i blokbogstaver`] = '';
		data[`Skattepligtig i land${i === 0 ? '' : `_${i + 1}`}`] = '';
		data[`Nationalitet${i === 0 ? '' : `_${i + 1}`}`] = '';
	}

	signatures.slice(0, 3).forEach((owner, i) => {
		data[`CPRnummer${i === 0 ? '' : `_${i + 1}`}`] = emptyToNull(owner.ssn);
		data[`Navn ${i + 1} i blokbogstaver`] = emptyToNull(owner.name);
		data[`Skattepligtig i land${i === 0 ? '' : `_${i + 1}`}`] = emptyToNull(owner.taxationCountry);
		data[`Nationalitet${i === 0 ? '' : `_${i + 1}`}`] = emptyToNull(owner.livingCountry);
	});

	for (let i = 1; i <= 4; i += 1) {
		data[`NavnRow${i}`] = '';
		data[`CPR nrRow${i}`] = '';
		data[`Ejerandel i Row${i}`] = '';
		data[`Skattepligtig i landRow${i}`] = '';
		data[`Nationalitet ${i}`] = '';
		data[i === 1 ? 'Ja' : `Ja_${i}`] = null;
		data[i === 1 ? 'Nej' : `Nej_${i}`] = null;
	}

	beneficialOwners.slice(0, 4).forEach((owner, index) => {
		const row = index + 1;
		data[`NavnRow${row}`] = emptyToNull(owner.name);
		data[`CPR nrRow${row}`] = emptyToNull(owner.ssn);
		data[`Ejerandel i Row${row}`] = emptyToNull(owner.ownership);
		data[`Skattepligtig i landRow${row}`] = emptyToNull(owner.taxationCountry);
		data[`Nationalitet ${row}`] = emptyToNull(owner.livingCountry);

		const yesKey = row === 1 ? 'Ja' : `Ja_${row}`;
		const noKey = row === 1 ? 'Nej' : `Nej_${row}`;

		if (owner.pep === true) {
			data[yesKey] = true;
			data[noKey] = null;
		} else if (owner.pep === false) {
			data[yesKey] = null;
			data[noKey] = true;
		}
	});

	data[NO_BENEFICIAL_OWNER_FIELD] = beneficialOwners.length === 0;

	if (contactPerson) {
		data['Kontaktperson'] = emptyToNull(contactPerson.name);
		data['contact-person-id'] = contactPerson.id;
	} else {
		data['Kontaktperson'] = '';
		data['contact-person-id'] = null;
	}

	if (bankAccount) {
		const registrationNumber = emptyToNull(bankAccount.registrationNumber);
		data['Reg nr'] = registrationNumber;
		data['Kontonummer'] = emptyToNull(bankAccount.accountNumber);
		data['Pengeinstituttets navn'] = BANK_NAMES[registrationNumber] || emptyToNull(bankAccount.name);
	} else {
		data['Reg nr'] = '';
		data['Kontonummer'] = '';
		data['Pengeinstituttets navn'] = '';
	}

	return data;
}
